import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import LookupPicker from '../additional_payments/lookup_picker';
import BulkModal from './bulk_modal';
import FreePassBox from './free_pass_box';
import PenaltyTable from './penalty_table';
import { RowDialogs, row_dialog, useRowWrite } from './row_dialogs';
import { useRowRefocus } from './row_focus';
import {
  fetch_additional_sessions,
  fetch_additional_teachers,
  fetch_penalties_free_pass,
  fetch_penalties_list,
  fetch_penalties_summary,
  post_penalties_bulk
} from '../api';
import { api_error, is_permission_error } from '../format';
import { api_pay_month } from '../additional_payments/params';
import {
  append_page,
  bulk_selection,
  free_pass_view,
  list_view,
  other_month_text,
  selection_total_text,
  summary_view
} from './adapters';
import {
  BULK_ACTION,
  DIRECTIVE_ACTION,
  ERROR,
  FILTER_BOOL,
  PENALTY_TYPES,
  ROW_ACTION,
  SOURCE,
  WAIVER_STATUS
} from './params';
import { bulk_payload, free_pass_params, list_params, summary_params } from './requests';
import './penalties.css';

/*
 * PROD-7828 R6 — Penalties tab: every penalty of the shell's month across teachers, with the money
 * split of brief §4.2, per-row Correct / Waive / Revert / Remove and the free-pass directive, bulk waive
 * and bulk "use a free pass", and "+ Add penalty". Every decision lives in params / requests / adapters /
 * error_handlers; this file is the I/O shell. After any write the list, the summary and the free-pass box
 * re-read, and the shell's badges refresh (on_changed). The shell keys this tab by month, so a month
 * change starts clean: filters, selection and messages belong to the month they were made in.
 */
const RESULT_BANNER = {
  success: 'tp-banner-ok',
  warning: 'tp-banner-warn',
  info: 'tp-banner-info'
};

const NO_FILTERS = {
  teacher_uuid: '',
  teacher_name: '',
  penalty_type: '',
  waiver_status: '',
  source: '',
  overridden: '',
  orphaned: ''
};

const LIST_LOAD_FAILED_TEXT = 'Could not load the penalties.';
const SUMMARY_LOAD_FAILED_TEXT = 'Could not load the month totals.';
const FREE_PASS_LOAD_FAILED_TEXT = "Could not load this teacher's free passes.";

// Only a LOOKUP_FAILED message is an Ops sentence (CD-144); the retry button beside it says "Try again".
const load_failed_text = (e, fallback) => {
  const { code, message } = api_error(e);
  return (code === ERROR.LOOKUP_FAILED && message.replace(/\s*try again\.?\s*$/i, '')) || fallback;
};

const PenaltiesTab = ({
  pay_month,
  month_label: month_text,
  month_badge,
  on_error,
  on_changed,
  new_request_id = uuidv4
}) => {
  const [drafts, setDrafts] = useState(NO_FILTERS);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [view, setView] = useState(() => list_view(null));
  const [list_state, setListState] = useState({ status: 'loading', error_text: '' });
  const [more_busy, setMoreBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summary_error, setSummaryError] = useState('');
  const [fp, setFp] = useState({ view: null, error_text: '' });
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [banner, setBanner] = useState('');
  const [highlight_uuid, setHighlight] = useState('');
  const seq = useRef(0);
  const scope_ref = useRef(null);
  const heading_ref = useRef(null);
  const refocus = useRowRefocus(scope_ref, heading_ref, view.rows);
  const month_closed = !!month_badge && month_badge.key === 'CLOSED';

  const fail_read = useCallback(
    (e) => {
      if (is_permission_error(e)) on_error(e);
    },
    [on_error]
  );

  // keep: a reload after a write leaves the rows on screen (and the opener's focus) until the new ones land.
  const load = useCallback(
    (keep = false) => {
      seq.current += 1;
      const mine = seq.current;
      if (!keep) setListState((s) => ({ ...s, status: 'loading' }));
      fetch_penalties_list(list_params(pay_month, filters))
        .then((data) => {
          if (mine !== seq.current) return;
          setView(list_view(data));
          setListState({ status: 'ok', error_text: '' });
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          fail_read(e);
          setView(list_view(null));
          setListState({ status: 'error', error_text: load_failed_text(e, LIST_LOAD_FAILED_TEXT) });
        });
      fetch_penalties_summary(summary_params(pay_month))
        .then((data) => {
          if (mine !== seq.current) return;
          setSummary(summary_view(data));
          setSummaryError('');
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          fail_read(e);
          setSummary(null);
          setSummaryError(load_failed_text(e, SUMMARY_LOAD_FAILED_TEXT));
        });
      if (filters.teacher_uuid) {
        fetch_penalties_free_pass(free_pass_params(filters.teacher_uuid, pay_month))
          .then((data) => {
            if (mine === seq.current) setFp({ view: free_pass_view(data), error_text: '' });
          })
          .catch((e) => {
            if (mine !== seq.current) return;
            fail_read(e);
            setFp({ view: null, error_text: load_failed_text(e, FREE_PASS_LOAD_FAILED_TEXT) });
          });
      } else {
        setFp({ view: null, error_text: '' });
      }
    },
    [pay_month, filters, fail_read]
  );

  useEffect(() => {
    load();
  }, [load]);

  const load_more = () => {
    if (more_busy || view.next_after_id === null) return;
    const mine = seq.current;
    setMoreBusy(true);
    fetch_penalties_list(list_params(pay_month, filters, view.next_after_id))
      .then((data) => {
        if (mine === seq.current) setView((v) => append_page(v, list_view(data)));
      })
      .catch((e) => {
        fail_read(e);
        if (mine === seq.current) setBanner(load_failed_text(e, LIST_LOAD_FAILED_TEXT));
      })
      .finally(() => setMoreBusy(false));
  };

  const apply_filters = () => {
    setSelected({});
    setFilters(drafts);
  };

  const focus_heading = () => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  };

  // A write's result lands in the tab banner; a refusal stays in the dialog that sent it (B7).
  const w = useRowWrite({
    on_saved: (r) => {
      setResult({ ...r, other_month_text: other_month_text(r.penalty, pay_month) });
      setBanner('');
      setHighlight(r.penalty && !r.removed ? r.penalty.uuid : '');
      refocus.arm();
      load(true);
      on_changed();
    },
    on_reload: () => load(true),
    on_permission: on_error
  });

  const open_pass = (action, teacher_uuid, teacher_name, day) => {
    setBanner('');
    fetch_penalties_free_pass(free_pass_params(teacher_uuid, pay_month))
      .then((data) =>
        w.open({ kind: 'pass', action, state: free_pass_view(data), day, teacher_name })
      )
      .catch((e) => {
        fail_read(e);
        setBanner(load_failed_text(e, FREE_PASS_LOAD_FAILED_TEXT));
      });
  };

  const on_row_action = (action, row) => {
    refocus.remember(row.uuid);
    const next = row_dialog(action, row);
    if (next) w.open(next);
    else if (action === ROW_ACTION.APPLY_PASS) {
      open_pass(DIRECTIVE_ACTION.APPLY, row.teacher_uuid, row.teacher_name, row.day_ist);
    } else if (action === ROW_ACTION.REMOVE_PASS) {
      open_pass(DIRECTIVE_ACTION.REMOVE, row.teacher_uuid, row.teacher_name, row.day_ist);
    }
  };

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );
  // month: the Add dialog's own month ("YYYY-MM"), which a CD-184 move can change.
  const search_sessions = useCallback(
    (teacher_uuid, q, month) =>
      teacher_uuid
        ? fetch_additional_sessions({ teacher_uuid, pay_month: api_pay_month(month), q }).then(
            (d) => (Array.isArray(d.rows) ? d.rows : [])
          )
        : Promise.resolve([]),
    []
  );

  const selected_rows = view.rows.filter((r) => selected[r.uuid]);
  const toggle = (r) => setSelected((s) => ({ ...s, [r.uuid]: !s[r.uuid] }));
  const toggle_all = (on) =>
    setSelected(on ? Object.fromEntries(view.rows.map((r) => [r.uuid, true])) : {});
  const open_bulk = (action) => {
    const { eligible, skipped } = bulk_selection(selected_rows, action);
    w.open({ kind: 'bulk', action, rows: eligible, skipped });
  };

  const set_draft = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));
  const add_off = month_closed;

  return (
    <div className="tp-pe" ref={scope_ref}>
      {month_closed ? (
        <div className="tp-banner tp-banner-info">
          {month_text} is closed for every teacher. Reopen a teacher&apos;s month under Needs
          attention to change its penalties.
        </div>
      ) : null}
      {summary_error ? (
        <LoadFailed text={summary_error} on_retry={() => load()} />
      ) : summary ? (
        <>
          <div className="tp-stats">
            {summary.totals.map((c) => (
              <div key={c.key} className="tp-stat">
                <div className="tp-stat-label">{c.label}</div>
                <div className={`tp-stat-value${c.tone === 'neg' ? ' tp-neg' : ''}`}>{c.value}</div>
                <span className="tp-stat-chip">{c.sub}</span>
              </div>
            ))}
          </div>
          {summary.cards.length ? (
            <div className="tp-pe-types">
              {summary.cards.map((c) => (
                <div key={c.key} className="tp-stat">
                  <div className="tp-stat-label">{c.label}</div>
                  <div className="tp-stat-value">{c.value}</div>
                  <span className="tp-stat-chip">{c.sub}</span>
                  {c.chips.map((chip) => (
                    <div key={chip} className="tp-card-field-label">
                      {chip}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {result ? (
        <div className={`tp-banner ${RESULT_BANNER[result.tone]}`} role="status">
          {result.message}
          {result.warnings.map((w) => (
            <div key={w.code}>{w.message}</div>
          ))}
          {result.issues_text ? <div>{result.issues_text}</div> : null}
          {result.other_month_text ? <div>{result.other_month_text}</div> : null}
        </div>
      ) : null}
      {banner ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {banner}
        </div>
      ) : null}

      <div className="tp-frow">
        <div className="tp-pe-filter-teacher">
          <LookupPicker
            id="tp-pe-filter-teacher"
            name="teacher filter"
            label="Filter by teacher"
            label_hidden
            placeholder="Filter by name or email"
            min_chars={2}
            value_text={drafts.teacher_name}
            search={search_teachers}
            option_key={(t) => t.teacher_uuid}
            render_option={(t) => (
              <span>
                <b>{t.name}</b> · {t.email}
              </span>
            )}
            on_pick={(t) =>
              setDrafts((d) => ({ ...d, teacher_uuid: t.teacher_uuid, teacher_name: t.name }))
            }
            on_clear={() => setDrafts((d) => ({ ...d, teacher_uuid: '', teacher_name: '' }))}
          />
        </div>
        <select
          className="tp-select"
          aria-label="Filter by penalty type"
          value={drafts.penalty_type}
          onChange={(e) => set_draft('penalty_type', e.target.value)}
        >
          <option value="">All types</option>
          {PENALTY_TYPES.map((t) => (
            <option key={t.penalty_type} value={t.penalty_type}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          aria-label="Filter by status"
          value={drafts.waiver_status}
          onChange={(e) => set_draft('waiver_status', e.target.value)}
        >
          <option value="">All statuses</option>
          <option value={WAIVER_STATUS.NOT_WAIVED}>Not waived</option>
          <option value={WAIVER_STATUS.WAIVED}>Waived</option>
          <option value={WAIVER_STATUS.FREE_PASS_APPLIED}>Free pass applied</option>
        </select>
        <select
          className="tp-select"
          aria-label="Filter by source"
          value={drafts.source}
          onChange={(e) => set_draft('source', e.target.value)}
        >
          <option value="">System and Ops-added</option>
          <option value={SOURCE.SYSTEM}>System</option>
          <option value={SOURCE.MANUAL}>Added by Ops</option>
        </select>
        <select
          className="tp-select"
          aria-label="Filter by Ops changes"
          value={drafts.overridden}
          onChange={(e) => set_draft('overridden', e.target.value)}
        >
          <option value="">Changed or not</option>
          <option value={FILTER_BOOL.TRUE}>Changed by Ops</option>
          <option value={FILTER_BOOL.FALSE}>Not changed</option>
        </select>
        <select
          className="tp-select"
          aria-label="Filter by orphaned"
          value={drafts.orphaned}
          onChange={(e) => set_draft('orphaned', e.target.value)}
        >
          <option value="">Orphaned or not</option>
          <option value={FILTER_BOOL.TRUE}>Orphaned only</option>
          <option value={FILTER_BOOL.FALSE}>Not orphaned</option>
        </select>
        <button type="button" className="tp-btn-out" onClick={apply_filters}>
          Apply
        </button>
        <div className="tp-frow-right">
          <button
            type="button"
            className="tp-btn"
            onClick={add_off ? undefined : () => w.open({ kind: 'add' })}
            aria-disabled={add_off ? 'true' : undefined}
            title={add_off ? `${month_text} is closed.` : ''}
          >
            + Add penalty
          </button>
        </div>
      </div>

      {filters.teacher_uuid && fp.error_text ? (
        <LoadFailed text={fp.error_text} on_retry={() => load()} />
      ) : null}
      {filters.teacher_uuid && fp.view ? (
        <FreePassBox
          view={fp.view}
          teacher_name={filters.teacher_name}
          on_action={(action, day) => {
            refocus.remember('');
            w.open({
              kind: 'pass',
              action,
              state: fp.view,
              day,
              teacher_name: filters.teacher_name
            });
          }}
        />
      ) : null}

      <div className="tp-card">
        {selected_rows.length ? (
          <div className="tp-bulkbar" role="region" aria-label="Selected penalties">
            <span>
              <b>{selected_rows.length}</b> selected · charged {selection_total_text(selected_rows)}
            </span>
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => open_bulk(BULK_ACTION.WAIVE)}
            >
              Waive selected
            </button>
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => open_bulk(BULK_ACTION.APPLY_FREE_PASS)}
            >
              Use free passes
            </button>
            <button type="button" className="tp-link" onClick={() => setSelected({})}>
              Clear
            </button>
          </div>
        ) : null}
        <div className="tp-card-hd">
          <span className="tp-card-hd-title" ref={heading_ref} tabIndex={-1}>
            Penalties · {month_text}
          </span>
          <span className="tp-card-hd-sub">
            {list_state.status === 'ok'
              ? `${view.rows.length} shown${view.next_after_id !== null ? ', more to load' : ''}`
              : ''}
          </span>
        </div>
        {list_state.status === 'loading' ? <CustomLoading /> : null}
        {list_state.status === 'error' ? (
          <LoadFailed text={list_state.error_text} on_retry={() => load()} />
        ) : null}
        {list_state.status === 'ok' ? (
          <PenaltyTable
            rows={view.rows}
            selected={selected}
            on_toggle={toggle}
            on_toggle_all={toggle_all}
            on_action={on_row_action}
            highlight_uuid={highlight_uuid}
            empty_text={`No penalties in ${month_text} for these filters.`}
          />
        ) : null}
      </div>
      {list_state.status === 'ok' && view.next_after_id !== null ? (
        <div className="tp-pe-more">
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={more_busy ? undefined : load_more}
            aria-disabled={more_busy ? 'true' : undefined}
          >
            {more_busy ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}

      <RowDialogs
        w={w}
        pay_month={pay_month}
        month_text={month_text}
        add={{
          new_request_id,
          search_teachers,
          search_sessions,
          // CD-217: the live policy map, or null while the summary hasn't loaded (the Add form leaves % empty).
          default_pcts: summary ? summary.default_pcts : null
        }}
        on_focus_lost={focus_heading}
      />
      {w.dialog && w.dialog.kind === 'bulk' ? (
        <BulkModal
          action={w.dialog.action}
          rows={w.dialog.rows}
          skipped={w.dialog.skipped}
          send={(rows, form, confirm_displace) =>
            post_penalties_bulk(bulk_payload(w.dialog.action, rows, form, confirm_displace))
          }
          on_finished={() => {
            setSelected({});
            load(true);
            on_changed();
          }}
          on_permission={on_error}
          on_close={w.close}
          on_focus_lost={focus_heading}
        />
      ) : null}
    </div>
  );
};

export default PenaltiesTab;
